import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ContentfulImg } from 'ha/modules/Contentful/components/ContentfulImg';

import { useIntl } from 'ha/i18n';
import { LazyComponent } from 'ha/components.legacy/LazyComponent';
import { Grid, Link, Paper, Typography, units } from '@ha/dsl';
import { ContentfulImage } from 'ha/modules/Hermes/types';

const useStyles = makeStyles()({
  root: {
    overflow: 'hidden',
    paddingBottom: units.rem(1.125),
    transition: 'transform 0.5s ease',
    height: '100%',
    '&:hover': {
      transform: 'scale(1.025)',
    },
  },
  imageWrapper: {
    position: 'relative',
    height: units.rem(15.25),
    marginBottom: units.rem(1),
  },
  imageCtr: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  image: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  name: {
    fontSize: units.rem(1.75),
    marginBottom: units.rem(1),
    zIndex: 1,
  },
  shadowForName: {
    top: '70%',
    bottom: 0,
    left: 0,
    right: 0,
    position: 'absolute',
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
  },
  listings: {
    fontWeight: 'bold',
    marginBottom: units.rem(0.5),
  },
  description: {
    padding: units.yx(0, 1),
  },
});

interface Props {
  listingsCount?: number;
  testLocator?: string;
  image: string;
  imageAsset?: ContentfulImage;
  name: string;
  description: string;
  url: string;
}

export const CityCard: React.FC<Props> = ({
  image,
  imageAsset,
  name,
  url,
  listingsCount = 0,
  description,
  testLocator,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();

  return (
    <Link to={url} data-test-locator={testLocator}>
      <Paper className={classes.root}>
        <Grid
          container
          alignItems="flex-end"
          justifyContent="center"
          className={classes.imageWrapper}
        >
          <LazyComponent bottomOffset="-200px" scrollableAncestor="window">
            <div className={classes.imageCtr}>
              <ContentfulImg
                // Remove isString check after https://github.com/housinganywhere/src/pull/1505 got merged.
                src={imageAsset ? imageAsset.fields.url : image}
                alt={name}
                format="webp"
                role="presentation"
                sizes={{ xs: '320px' }}
                isResponsive={false}
                className={classes.image}
              />
            </div>
          </LazyComponent>
          <Typography.H3 className={classes.name} color="white">
            {name}
          </Typography.H3>
          <div className={classes.shadowForName} />
        </Grid>
        {listingsCount !== 0 && (
          <Typography.Caption
            align="center"
            color="muted"
            className={classes.listings}
          >
            {listingsCount} {T('Listings')}
          </Typography.Caption>
        )}
        <Typography.Paragraph align="center" className={classes.description}>
          {description}
        </Typography.Paragraph>
      </Paper>
    </Link>
  );
};
